import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import FloatingNotes from '@/components/FloatingNotes';import { ChartLine, ChatDots, Package, FileText, Gear, List, SignOut, UserGear, ChartBar, Bell, MessengerLogo, WhatsappLogo, X, BookOpen, Lightning, CaretDown, Trophy, IdentificationBadge, MapPinArea, Briefcase, Target, Sun, Moon, Sparkle, Cpu, Funnel, Receipt, Note, ChatCircleDots, Cube, Brain, Hammer, Calendar, Robot, Database, Storefront, Globe, ShareNetwork, Ruler } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import api from '@/api/axios';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === 'undefined' || window.innerWidth >= 768);
  const [permissions, setPermissions] = useState([]);
  useEffect(() => {
    api.get('/sa/mis-permisos').then((r) => setSaModulo(!!r.data?.modulo)).catch(() => {});
  }, []);
  const [tabPermissions, setTabPermissions] = useState({});
  const [deniedModules, setDeniedModules] = useState([]);
  const [saModulo, setSaModulo] = useState(false);

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [inactiveAlertsCount, setInactiveAlertsCount] = useState(0);
  const [predictiveAlertsCount, setPredictiveAlertsCount] = useState(0);
  const [totalUnread, setTotalUnread] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [showBizDropdown, setShowBizDropdown] = useState(false);
  const prevUnreadRef = useRef({});
  const notifPanelRef = useRef(null);

  useEffect(() => {
    fetchBusinesses();
  }, []);

  useEffect(() => {
    if (selectedBusiness) {
      fetchPermissions();
    }
  }, [selectedBusiness, isAdmin]);

  // Poll for notifications every 3 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 3000);
    return () => clearInterval(interval);
  }, []);

  // Poll for inactive vendor alerts every 60 seconds (admins + supervisors only)
  useEffect(() => {
    if (!isAdmin && user?.role !== 'supervisor') return;
    const fetchAlerts = async () => {
      try {
        const res = await api.get('/work-plan/alerts/inactive-vendors');
        setInactiveAlertsCount(res.data.count || 0);
      } catch {
        // silently ignore
      }
    };
    fetchAlerts();
    const t = setInterval(fetchAlerts, 60000);
    return () => clearInterval(t);
  }, [isAdmin, user?.role]);

  // Poll predictive AI alerts badge for ANY authenticated user (role-filtered server-side)
  useEffect(() => {
    if (!user) return;
    const fetchPred = async () => {
      try {
        const res = await api.get('/predictive-alerts/badge');
        setPredictiveAlertsCount(res.data?.total || 0);
      } catch {
        // silently ignore
      }
    };
    fetchPred();
    const t = setInterval(fetchPred, 120000);
    return () => clearInterval(t);
  }, [user]);

  // Close panels on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (notifPanelRef.current && !notifPanelRef.current.contains(e.target)) {
        setShowNotifPanel(false);
      }
      if (showBizDropdown) {
        const sidebar = document.querySelector('[data-testid="sidebar"]');
        if (sidebar && !sidebar.contains(e.target)) {
          setShowBizDropdown(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showBizDropdown]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread');
      const data = res.data;
      setNotifications(data.notifications || []);
      setTotalUnread(data.total_unread || 0);

      for (const notif of (data.notifications || [])) {
        const prevCount = prevUnreadRef.current[notif.business_id] || 0;
        if (notif.unread_count > prevCount && prevCount >= 0) {
          const newMsgs = notif.unread_count - prevCount;
          if (prevCount > 0 || Object.keys(prevUnreadRef.current).length > 0) {
            toast.info(
              `${notif.business_name}: ${newMsgs} mensaje${newMsgs > 1 ? 's' : ''} nuevo${newMsgs > 1 ? 's' : ''} de ${notif.latest_customer || 'cliente'}`,
              { duration: 6000 }
            );
          }
        }
      }

      const newPrev = {};
      for (const n of (data.notifications || [])) {
        newPrev[n.business_id] = n.unread_count;
      }
      prevUnreadRef.current = newPrev;
    } catch {
      // silently fail
    }
  }, []);

  const fetchChatUnread = useCallback(async () => {
    try {
      const { data } = await api.get('/chat/unread');
      setChatUnread(data?.unread || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchChatUnread();
    const intv = setInterval(fetchChatUnread, 30000); // every 30s, lightweight
    return () => clearInterval(intv);
  }, [fetchChatUnread]);

  const fetchBusinesses = async () => {
    try {
      const response = await api.get('/users/me/businesses');
      const bizList = response.data || [];
      setBusinesses(bizList);
      if (bizList.length > 0) {
        if (!selectedBusiness || !bizList.find(b => b.id === selectedBusiness.id)) {
          setSelectedBusiness(bizList[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching businesses:', error);
    }
  };

  const fetchPermissions = async () => {
    if (!selectedBusiness) return;
    try {
      const response = await api.get(`/users/me/permissions/${selectedBusiness.id}`);
      setPermissions(response.data.permissions || []);
      setTabPermissions(response.data.tab_permissions || {});
      setDeniedModules(response.data.denied_modules || []);
    } catch {
      setPermissions([]);
      setTabPermissions({});
    }
  };

  // Returns true if user has access to the given tab within a module.
  // If tab_permissions[module] is undefined → all tabs allowed (default = backwards compat).
  // If tab_permissions[module] is an array → only those tabs are allowed.
  const hasTabPermission = (module, tab) => {
    if (isAdmin) return true;
    const tabs = tabPermissions?.[module];
    if (!tabs) return true;
    return tabs.includes(tab);
  };

  const hasPermission = (module) => {
    // Módulos ocultados globalmente (o denegados) aplican también al admin
    if (module && deniedModules.includes(module)) return false;
    if (isAdmin) return true;
    if (!module) return false; // adminOnly items without a module are blocked for non-admins
    if (module === 'mi_dia') return true; // All authenticated users see their own day
    if (module === 'stock') return true; // Búsqueda rápida de stock — disponible a todos
    if (module === 'compartir') return true; // Links y QR para compartir — visible a todos los vendedores
    // Producción — solo roles de producción/supervisión o permiso explícito por negocio
    if (module === 'produccion') {
      const r = (user?.role || '').toLowerCase();
      if (['admin', 'supervisor', 'gerente', 'produccion', 'operaciones', 'financiero'].includes(r)) return true;
      return permissions.includes('produccion');
    }
    if (module === 'clientes_vendedores') return true; // Every vendor sees their own captured clients
    if (module === 'clientes') return true; // Ficha maestra de clientes — visible a todos
    // Presupuestos SA — administración/gerencia siempre; usuarios con permiso global sa_permisos.modulo o por negocio
    if (module === 'presupuestos_sa') {
      const r = (user?.role || '').toLowerCase();
      if (['admin', 'supervisor', 'gerente', 'financiero'].includes(r)) return true;
      return saModulo || permissions.includes('presupuestos_sa');
    }
    if (module === 'leads_landing') return permissions.includes('leads_landing'); // Solo admin (línea isAdmin arriba) o permiso explícito
    // Instaladores — solo roles de producción/supervisión (NO vendedores), o permiso explícito
    if (module === 'instaladores') {
      const r = (user?.role || '').toLowerCase();
      if (['admin', 'supervisor', 'gerente', 'produccion', 'operaciones'].includes(r)) return true;
      return permissions.includes('instaladores');
    }
    // Supervisor-default modules also accept explicit per-business permission
    if (['plan_trabajo', 'ranking', 'vendors', 'coverage', 'reportes', 'reports'].includes(module)) {
      if (user?.role === 'supervisor') return true;
      return permissions.includes(module);
    }
    if (module === 'inbox') return permissions.includes('inbox') || permissions.includes('inbox_asignado');
    // Everything else (including dashboard_gerencial, alertas, wa_campaigns, asistente_ia,
    // pendientes, chat_interno, cotizador) is gated by per-business permission.
    return permissions.includes(module.toLowerCase());
  };

  const handleNotifClick = (notif) => {
    if (notif.business_id === 'leads_landing') {
      setShowNotifPanel(false);
      setSidebarOpen(false);
      navigate('/leads-landing');
      return;
    }
    const biz = businesses.find(b => b.id === notif.business_id);
    if (biz) {
      setSelectedBusiness(biz);
    }
    setShowNotifPanel(false);
    setSidebarOpen(false);
    if (location.pathname === '/inbox') {
      navigate('/dashboard', { replace: true });
      setTimeout(() => navigate('/inbox'), 50);
    } else {
      navigate('/inbox');
    }
  };

  const unreadByBusiness = {};
  for (const n of notifications) {
    unreadByBusiness[n.business_id] = n.unread_count;
  }

  const menuItems = [
    // Mi Trabajo — lo que se usa a diario
    { path: '/mi-dia', icon: Briefcase, label: 'Mi Día', testId: 'nav-mi-dia', module: 'mi_dia', group: 'Mi Trabajo' },
    { path: '/cotizador', icon: Receipt, label: 'Cotizador', testId: 'nav-cotizador', module: 'cotizador', group: 'Mi Trabajo' },
    { path: '/stock', icon: Cube, label: 'Stock', testId: 'nav-stock', module: 'stock', group: 'Mi Trabajo' },
    { path: '/pendientes', icon: Note, label: 'Pendientes', testId: 'nav-pendientes', module: 'pendientes', group: 'Mi Trabajo' },
    { path: '/chat', icon: ChatCircleDots, label: 'Chat interno', testId: 'nav-chat', module: 'chat_interno', group: 'Mi Trabajo' },
    { path: '/compartir', icon: ShareNetwork, label: 'Compartir Links', testId: 'nav-compartir', module: 'compartir', group: 'Mi Trabajo' },
    // Redes Sociales — Messenger / WhatsApp
    { path: '/inbox', icon: ChatDots, label: 'Mensajes', testId: 'nav-inbox', module: 'inbox', group: 'Redes Sociales' },
    { path: '/dashboard', icon: ChartLine, label: 'Dashboard Mensajes', testId: 'nav-dashboard', module: 'dashboard', group: 'Redes Sociales' },
    { path: '/orders', icon: Package, label: 'Pedidos', testId: 'nav-orders', module: 'orders', group: 'Redes Sociales' },
    { path: '/campanas', icon: WhatsappLogo, label: 'Campañas WhatsApp', testId: 'nav-campanas', module: 'wa_campaigns', group: 'Redes Sociales' },
    { path: '/clientes-redes', icon: ChatDots, label: 'Clientes Redes', testId: 'nav-clientes-redes', module: 'clientes_redes', group: 'Redes Sociales' },
    { path: '/templates', icon: FileText, label: 'Plantillas', testId: 'nav-templates', module: 'templates', group: 'Redes Sociales' },
    // Ventas — seguimiento comercial
    { path: '/reports', icon: ChartBar, label: 'Reporte de Ventas', testId: 'nav-reports', module: 'reports', group: 'Ventas' },
    { path: '/plan-trabajo', icon: Target, label: 'Plan de Trabajo', testId: 'nav-plan-trabajo', module: 'plan_trabajo', group: 'Ventas' },
    { path: '/reportes', icon: ChartBar, label: 'Metas vs Ventas', testId: 'nav-reportes', module: 'reportes', group: 'Ventas' },
    { path: '/reporte-actividades', icon: Funnel, label: 'Reporte Actividades', testId: 'nav-reporte-actividades', module: 'reportes', group: 'Ventas' },
    { path: '/ranking', icon: Trophy, label: 'Top Vendedores', testId: 'nav-ranking', module: 'ranking', group: 'Ventas' },
    { path: '/prospectos', icon: Sparkle, label: 'Prospectos IA', testId: 'nav-prospectos', module: 'prospectos', group: 'Ventas' },
    { path: '/clientes', icon: IdentificationBadge, label: 'Clientes', testId: 'nav-clientes', module: 'clientes', group: 'Clientes' },
    { path: '/clientes-vendedores', icon: Briefcase, label: 'Clientes Vendedores', testId: 'nav-clientes-vendedores', module: 'clientes_vendedores', group: 'Ventas' },
    { path: '/coverage', icon: MapPinArea, label: 'Cobertura', testId: 'nav-coverage', module: 'coverage', group: 'Ventas' },
    // Producción e instalaciones
    { path: '/produccion', icon: Hammer, label: 'Producción', testId: 'nav-produccion', module: 'produccion', group: 'Producción' },
    { path: '/programacion', icon: Calendar, label: 'Programación', testId: 'nav-programacion', module: 'programacion', group: 'Producción' },
    { path: '/instaladores', icon: Hammer, label: 'Instaladores', testId: 'nav-instaladores', module: 'instaladores', group: 'Producción' },
    { path: '/presupuestos-sa', icon: Ruler, label: 'Presupuestos SA', testId: 'nav-presupuestos-sa', module: 'presupuestos_sa', group: 'Producción' },
    { path: '/biblioteca-simbolos', icon: Ruler, label: 'Biblioteca de Símbolos', testId: 'nav-biblioteca-simbolos', module: 'presupuestos_sa', group: 'Producción' },
    // Inteligencia IA
    { path: '/asistente-ia', icon: Brain, label: 'Asistente IA', testId: 'nav-asistente-ia', module: 'asistente_ia', group: 'Inteligencia IA' },
    { path: '/chat-ia', icon: Robot, label: 'Chat IA', testId: 'nav-chat-ia', module: 'chat_ia', group: 'Inteligencia IA' },
    { path: '/alertas', icon: Bell, label: 'Alertas IA', testId: 'nav-alertas', module: 'alertas', group: 'Inteligencia IA' },
    { path: '/dashboard-gerencial', icon: Target, label: 'Dashboard Gerencial', testId: 'nav-dashboard-gerencial', module: 'dashboard_gerencial', group: 'Inteligencia IA' },
    { path: '/dashboard-comercial', icon: ChartBar, label: 'Dashboard Comercial', testId: 'nav-dashboard-comercial', module: 'dashboard_comercial', group: 'Inteligencia IA' },
    { path: '/ai-costos', icon: Cpu, label: 'Costos IA', testId: 'nav-ai-costos', module: 'ai_costos', group: 'Inteligencia IA' },
    // Administración
    { path: '/catalog', icon: BookOpen, label: 'Catálogo', testId: 'nav-catalog', module: 'catalogo', group: 'Administración' },
    { path: '/tienda-admin', icon: Storefront, label: 'Tienda en Línea', testId: 'nav-tienda-admin', module: 'tienda_admin', group: 'Administración' },
    { path: '/landing-pages', icon: Globe, label: 'Landing Pages', testId: 'nav-landing-pages', module: 'landing_pages', group: 'Administración' },
    { path: '/leads-landing', icon: Funnel, label: 'Leads Landing', testId: 'nav-leads-landing', module: 'leads_landing', group: 'Administración' },
    { path: '/harperp', icon: Database, label: 'HarpERP', testId: 'nav-harperp', module: 'harperp', group: 'Administración' },
    { path: '/vendors', icon: IdentificationBadge, label: 'Vendedores', testId: 'nav-vendors', module: 'vendors', group: 'Administración' },
    { path: '/settings', icon: Gear, label: 'Configuración', testId: 'nav-settings', module: 'settings', group: 'Administración' },
  ];

  if (isAdmin) {
    menuItems.push({
      path: '/admin',
      icon: UserGear,
      label: 'Administración',
      testId: 'nav-admin',
      module: 'admin',
      group: 'Administración',
    });
  }

  const filteredMenuItems = menuItems.filter((item) => {
    if (item.adminOnly && !isAdmin) return false;
    return hasPermission(item.module);
  });
  const GROUP_ORDER = ['Mi Trabajo', 'Clientes', 'Redes Sociales', 'Ventas', 'Producción', 'Inteligencia IA', 'Administración'];
  const grouped = GROUP_ORDER.map((g) => ({
    name: g,
    items: filteredMenuItems.filter((i) => i.group === g),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="flex h-screen text-white" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed md:relative z-50 h-full flex flex-col transition-all duration-200 border-r
          ${sidebarOpen ? 'w-72 translate-x-0' : 'hidden md:flex w-0 md:w-20'}
        `}
        style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
        data-testid="sidebar"
      >
        {/* Header */}
        <div className="relative shrink-0 min-w-[280px]">
          <div className="p-5 flex items-center justify-between">
            {sidebarOpen && (
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-[12px] flex items-center justify-center shadow-sm" style={{ background: 'var(--primary)' }}>
                  <Lightning size={20} weight="fill" className="text-white" />
                </div>
                <h1 className="text-xl font-black tracking-tight heading-font" style={{ color: 'var(--text)' }}>
                  SACH<span style={{ color: 'var(--primary)' }}>.</span>
                </h1>
              </div>
            )}
            <div className="flex items-center gap-1">
              <div className="relative" ref={notifPanelRef}>
                <button
                  onClick={() => setShowNotifPanel(!showNotifPanel)}
                  className="relative p-2 transition-colors duration-200 rounded-[10px]"
                  style={{ color: totalUnread > 0 ? 'var(--primary)' : 'var(--text-muted)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-muted)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  data-testid="notification-bell"
                >
                  <Bell size={20} weight={totalUnread > 0 ? 'fill' : 'regular'} />
                  {totalUnread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-white text-[10px] font-black rounded-full px-1" style={{ background: 'var(--primary)' }}>
                      {totalUnread > 99 ? '99+' : totalUnread}
                    </span>
                  )}
                </button>

                {showNotifPanel && (
                  <div
                    className={`fixed top-14 z-[200] rounded-[16px] shadow-2xl overflow-hidden border ${sidebarOpen ? 'inset-x-2 md:inset-x-auto md:left-[290px] md:w-80' : 'inset-x-2 md:inset-x-auto md:left-[88px] md:w-80'}`}
                    style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
                    data-testid="notification-panel"
                  >
                    <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border)' }}>
                      <h3 className="text-sm font-bold heading-font" style={{ color: 'var(--text)' }}>Notificaciones</h3>
                      <button onClick={() => setShowNotifPanel(false)} className="transition-colors" style={{ color: 'var(--text-muted)' }}>
                        <X size={16} weight="bold" />
                      </button>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center">
                          <Bell size={36} className="mx-auto mb-2" style={{ color: 'var(--text-faint)' }} />
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Sin mensajes nuevos</p>
                        </div>
                      ) : (
                        notifications.map((notif) => (
                          <button
                            key={notif.business_id}
                            onClick={() => handleNotifClick(notif)}
                            className="w-full text-left p-4 border-b transition-colors"
                            style={{ borderColor: 'var(--border)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-muted)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            data-testid={`notif-item-${notif.business_id}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="mt-0.5">
                                {notif.latest_platform === 'whatsapp' ? (
                                  <WhatsappLogo size={20} weight="fill" className="text-green-500" />
                                ) : (
                                  <MessengerLogo size={20} weight="fill" className="text-blue-500" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-bold truncate" style={{ color: 'var(--text)' }}>{notif.business_name}</span>
                                  <span className="ml-2 min-w-[20px] h-5 flex items-center justify-center text-white text-[10px] font-black rounded-full px-1.5" style={{ background: 'var(--primary)' }}>
                                    {notif.unread_count}
                                  </span>
                                </div>
                                <p className="text-xs mt-1 truncate" style={{ color: 'var(--text-muted)' }}>
                                  <span className="font-medium" style={{ color: 'var(--text)' }}>{notif.latest_customer}:</span> {notif.latest_message}
                                </p>
                                <p className="text-[10px] mt-1" style={{ color: 'var(--text-faint)' }}>
                                  {notif.unread_conversations} conv. sin leer
                                </p>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="hidden md:flex p-2 transition-colors duration-200 rounded-[10px]"
                style={{ color: 'var(--text-muted)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-muted)'; e.currentTarget.style.color = 'var(--text)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                data-testid="sidebar-toggle-btn"
              >
                <List size={22} weight="bold" />
              </button>
            </div>
          </div>
        </div>

        {/* User Info */}
        {sidebarOpen && user && (
          <div className="mx-4 mt-1 p-3 rounded-[14px] border transition-all cursor-pointer"
            style={{ background: 'var(--surface-muted)', borderColor: 'var(--border)' }}
            onClick={() => navigate('/me')}
            data-testid="user-card-link"
            title="Mi perfil"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm" style={{ background: 'var(--primary)' }}>
                <span className="text-white font-black text-sm">
                  {user.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate" style={{ color: 'var(--text)' }}>{user.name}</p>
                <p className="text-[10px] uppercase tracking-[0.15em] truncate font-bold" style={{ color: 'var(--text-muted)' }}>
                  {isAdmin ? 'Administrador' : (user.role === 'supervisor' ? 'Supervisor' : user.role === 'community_manager' ? 'Community Manager' : user.role === 'gerente' ? 'Gerente' : 'Agente')} · Editar
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Business Selector */}
        {sidebarOpen && businesses.length > 0 && (
          <div className="mx-4 mt-3 mb-1">
            <label className="text-[10px] tracking-[0.2em] uppercase font-bold mb-2 block px-1" style={{ color: 'var(--text-muted)' }}>
              Negocio
            </label>
            <div className="relative">
              <button
                onClick={() => setShowBizDropdown(!showBizDropdown)}
                className="w-full text-sm p-3 border rounded-[12px] flex items-center justify-between transition-all text-left group"
                style={{ background: 'var(--surface-muted)', borderColor: 'var(--border)' }}
                data-testid="business-selector"
              >
                <span className="truncate flex-1 font-bold" style={{ color: 'var(--text)' }}>{selectedBusiness?.name || 'Seleccionar'}</span>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  {unreadByBusiness[selectedBusiness?.id] > 0 && (
                    <span className="min-w-[18px] h-[18px] flex items-center justify-center text-white text-[9px] font-black rounded-full px-1" style={{ background: 'var(--primary)' }}>
                      {unreadByBusiness[selectedBusiness?.id]}
                    </span>
                  )}
                  <CaretDown size={12} weight="bold" className={`transition-transform ${showBizDropdown ? 'rotate-180' : ''}`} style={{ color: 'var(--text-muted)' }} />
                </div>
              </button>
              {showBizDropdown && (
                <div className="absolute left-0 right-0 top-full mt-2 border rounded-[12px] shadow-xl z-50 max-h-64 overflow-y-auto"
                  style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
                >
                  {businesses.map((biz) => {
                    const unread = unreadByBusiness[biz.id] || 0;
                    const isSelected = selectedBusiness?.id === biz.id;
                    return (
                      <button
                        key={biz.id}
                        onClick={() => {
                          setSelectedBusiness(biz);
                          setShowBizDropdown(false);
                        }}
                        className="w-full text-left px-4 py-3 text-sm flex items-center justify-between transition-colors"
                        style={{
                          background: isSelected ? 'var(--primary-soft)' : 'transparent',
                          borderLeft: isSelected ? '3px solid var(--primary)' : '3px solid transparent',
                        }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--surface-muted)'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                        data-testid={`biz-option-${biz.id}`}
                      >
                        <span className="truncate flex-1 font-medium" style={{ color: isSelected ? 'var(--primary)' : 'var(--text)' }}>{biz.name}</span>
                        {unread > 0 && (
                          <span className="ml-2 min-w-[20px] h-5 flex items-center justify-center text-white text-[10px] font-black rounded-full px-1.5 shrink-0" style={{ background: 'var(--primary)' }}>
                            {unread}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {grouped.map((g) => (
            <div key={g.name} className="mb-5">
              {sidebarOpen && (
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold px-3 mb-2" style={{ color: 'var(--text-faint)' }}>
                  {g.name}
                </p>
              )}
              <div className="space-y-1">
                {g.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  const inboxUnread = item.path === '/inbox' ? totalUnread : 0;
                  const chatBadge = item.path === '/chat' ? chatUnread : 0;
                  const alertsBadge = item.path === '/plan-trabajo'
                    ? inactiveAlertsCount
                    : (item.path === '/alertas' ? predictiveAlertsCount : 0);
                  const badge = inboxUnread || chatBadge || alertsBadge;
                  const badgeColor = alertsBadge > 0 && !inboxUnread && !chatBadge ? '#DC2626' : 'var(--primary)';
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => { if (window.innerWidth < 768) setSidebarOpen(false); }}
                      className="group relative flex items-center gap-3 px-3 py-2.5 text-sm font-bold transition-all duration-200 rounded-[10px]"
                      style={{
                        background: isActive ? 'var(--primary-soft)' : 'transparent',
                        color: isActive ? 'var(--primary)' : 'var(--text-muted)',
                      }}
                      onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.background = 'var(--surface-muted)'; e.currentTarget.style.color = 'var(--text)'; } }}
                      onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; } }}
                      data-testid={item.testId}
                    >
                      <div className="w-7 h-7 flex items-center justify-center shrink-0">
                        <Icon size={18} weight={isActive ? 'fill' : 'bold'} />
                      </div>
                      {sidebarOpen && <span className="flex-1">{item.label}</span>}
                      {sidebarOpen && badge > 0 && (
                        <span className="ml-auto min-w-[22px] h-[22px] flex items-center justify-center text-[10px] font-black rounded-full px-1.5 text-white" style={{ background: badgeColor }} data-testid={`sidebar-badge-${item.testId}`}>
                          {badge > 99 ? '99+' : badge}
                        </span>
                      )}
                      {!sidebarOpen && badge > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-[9px] font-black rounded-full px-1 text-white" style={{ background: badgeColor }}>
                          {badge > 99 ? '99+' : badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Theme toggle + Logout */}
        <div className="p-3 border-t" style={{ borderColor: 'var(--border)' }}>
          {sidebarOpen && (
            <button
              onClick={toggleTheme}
              className="w-full flex items-center gap-3 px-3 py-2 text-xs font-bold rounded-[10px] mb-1 transition-colors"
              style={{ color: 'var(--text-muted)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-muted)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              data-testid="theme-toggle-btn"
            >
              {theme === 'light' ? <Moon size={16} weight="bold" /> : <Sun size={16} weight="bold" />}
              <span>Cambiar a {theme === 'light' ? 'oscuro' : 'claro'}</span>
            </button>
          )}
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-bold transition-all duration-200 rounded-[10px]"
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(192,57,43,0.1)'; e.currentTarget.style.color = 'var(--danger)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
            data-testid="logout-btn"
          >
            <div className="w-8 h-8 flex items-center justify-center shrink-0">
              <SignOut size={18} weight="bold" />
            </div>
            {sidebarOpen && <span>Cerrar Sesión</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto flex flex-col" style={{ background: 'var(--bg)' }}>
        <div className="flex-1 overflow-auto">
          {/* Mobile header */}
          {!sidebarOpen && (
            <div className="md:hidden sticky top-0 z-30 flex items-center gap-2 px-3 py-3 backdrop-blur-xl border-b" id="mobile-header-bar"
              style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
            >
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-[10px] transition-colors"
                style={{ color: 'var(--text)' }}
                data-testid="mobile-menu-btn"
              >
                <List size={22} weight="bold" />
              </button>
              <div className="flex items-center gap-2 flex-1">
                <div className="w-7 h-7 rounded-[8px] bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                  <Lightning size={16} weight="fill" className="text-white" />
                </div>
                <span className="text-sm font-heading font-black text-white">SACH<span className="text-blue-500">.</span></span>
              </div>
              <div className="relative" ref={notifPanelRef}>
                <button
                  onClick={() => setShowNotifPanel(!showNotifPanel)}
                  className="relative p-2 hover:bg-white/5 rounded-[12px] transition-colors"
                  data-testid="mobile-notification-bell"
                >
                  <Bell size={20} weight={totalUnread > 0 ? 'fill' : 'bold'} className={totalUnread > 0 ? 'text-red-500' : 'text-slate-400'} />
                  {totalUnread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] flex items-center justify-center bg-red-500 text-white text-[9px] font-black rounded-full px-0.5 shadow-[0_0_10px_rgba(239,68,68,0.6)]">
                      {totalUnread > 99 ? '99+' : totalUnread}
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}
          <Outlet context={{ selectedBusiness, setSelectedBusiness, fetchBusinesses, user, setSidebarOpen, totalUnread, tabPermissions, hasTabPermission }} />
        </div>
      </div>
      <FloatingNotes hideOnPaths={['/chat', '/asistente-ia']} />
    </div>
  );
};

export default Layout;
